
import React, { useState, createContext, useEffect, useContext } from 'react';
import Amplify, { Auth } from 'aws-amplify';
import { OrganizationContext } from './OrganizationProvider'
import environment from '../utils/environment'

Amplify.configure({
    // REQUIRED - Amazon Cognito Region
    region: environment.awsRegion,
    // OPTIONAL - Amazon Cognito User Pool ID
    userPoolId:  environment.cognitoUserPoolId,
    // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
    userPoolWebClientId: environment.cognitoWebClientId,
})

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
    const [isLoading, setIsloading] = useState(true)
    const [jwt, setJwt] = useState(null);
    const { organizationId, setOrganizationId, wasStorageChecked } = useContext(OrganizationContext)
    const [isSuperDuperAdmin, setIsSuperDuperAdmin] = useState(false)
    const [isAdminForOrganization, setIsAdminForOrganization] = useState(false)
    const [isVoterInOrganization, setIsVoterInOrganization] = useState(false)
    const [voterAlias, setVoterAlias] = useState(undefined)
    const [email, setEmail] = useState(undefined)
    const [organizations, setOrganizations] = useState([])


    useEffect(() => {
        const checkForLoggedIn = async () => {
            try {
                const resp = await Auth.currentAuthenticatedUser();
                await checkUserAndUpdateState(resp)
            }
            catch{
                // this happens if the user is not logged in
                setLoggedOutState()
            }
        }

        if (wasStorageChecked) {
            checkForLoggedIn()
        }
        
    }, [wasStorageChecked])

    useEffect(() => {
        if (jwt && organizationId) {
            checkAndUpdateUserOrganizationPermissions(jwt);
        }
    }, [organizationId])

    useEffect(() => {
        // check every so often to see if we need to refresh the logged in user
        console.log('attempting to refresh session if needed')
        const interval = setInterval(() => {
            Auth.currentSession().then(session => {
                setJwt(session.accessToken.jwtToken)
            })
        }, 1000 * 5)
        return () => clearInterval(interval)
    }, [])

    const setLoggedOutState = () => {
        setIsloading(false);
        setIsLoggedIn(false);
        setUser(null);
        setJwt(null);
    }

    const login = async (email, password) => {
        if(!email || !password)
            return
        setIsloading(true)
        try {
            setErrorMessage(null);
            const userResp = await Auth.signIn(email, password);
            await checkUserAndUpdateState(userResp);
        } catch (err) {
            console.error(err)
            if (err.code === "NotAuthorizedException") {
                // say something cute
            }
            setErrorMessage(err.message);
        }
        setIsloading(false)
    };

    // perhaps this could exist in another file
    const forgotPasswordSendEmail = async (email) => {
        setIsloading(true)
        try {
            setErrorMessage(null)
            const resp = await Auth.forgotPassword(email)
            setIsloading(false)
            const details = resp.CodeDeliveryDetails
            return details
        } catch (err) {
            setIsloading(false)
            console.log(err)
            setErrorMessage(err.message)
        }
    }

    // perhaps this could exist in another file
    const forgotPasswordSetNewPassword = async (email, token, newPassword) => {

        const userName = email
        const code = token


        setIsloading(true)
        try {
            setErrorMessage(null)
            const resp = await Auth.forgotPasswordSubmit(userName, code, newPassword)
            setIsloading(false)
            // const details = resp.CodeDeliveryDetails
            // return details
        } catch (err) {
            setIsloading(false)
            console.log(err)
            setErrorMessage(err.message)
        }
    }

    // perhaps this could exist in another file
    const changePassword = async (oldPassword, newPassword) => {
        setIsloading(true)

        try{
            const resp = await Auth.changePassword(user, oldPassword, newPassword)
            return { success: true }
        }catch(err){
            console.log(err)
            const message = err?.message ?? "Unable to change password, please contact support"
            return { success: false, message }
        }
    }

    const checkAndUpdateUserOrganizationPermissions = async (jwt) => {
        const permissionsResponse = await fetch(`${environment.backendUrl}/api/auth/${organizationId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jwt}`
            }
        }).then(x => x.json());
        setIsSuperDuperAdmin(permissionsResponse.isSuperDuperAdmin)
        setIsAdminForOrganization(permissionsResponse.isAdminForOrganization)
        setIsVoterInOrganization(permissionsResponse.isVoterInOrganization)
        setVoterAlias(permissionsResponse.voterAlias)
        setOrganizations(permissionsResponse.organizations)
        setEmail(permissionsResponse.email)

        return permissionsResponse;
    }

    const checkUserAndUpdateState = async (resp) => {
        console.log('checkForLoggedIn', resp)

        if (resp.signInUserSession) {
            if (resp != null) {
                const jwt = resp.signInUserSession.accessToken.jwtToken
                setJwt(jwt)

                const permissionsResponse = await checkAndUpdateUserOrganizationPermissions(jwt);

                const isUserPartOfOrganization = permissionsResponse.organizations.map(x => x.organizationId).includes(organizationId)
                if (!organizationId || !isUserPartOfOrganization) {
                    await setOrganizationId(permissionsResponse.organizations[0].organizationId)
                }
                setUser(resp);
                window.Auth = Auth
                window.user = resp
                setIsLoggedIn(true)
                setIsNewPasswordRequired(false);
            }
        } else if (resp.challengeName === "NEW_PASSWORD_REQUIRED") {
            setUser(resp);
            setIsNewPasswordRequired(true);
        }
    }



    return (
        <AuthContext.Provider
            value={{
                isLoggedIn,
                isSuperDuperAdmin,
                isAdminForOrganization,
                isVoterInOrganization,
                login,
                isNewPasswordRequired,
                voterAlias,
                organizations,

                // perhaps this could exist in another file
                completeNewPassword: async (password, name) => {
                    setIsloading(true)
                    try {
                        setErrorMessage(null);
                        console.log('currentuser', user);
                        const userResp = await Auth.completeNewPassword(user, password, { name });
                        console.log(userResp);
                        await checkUserAndUpdateState(userResp);
                    } catch (err) {
                        console.error(err)
                        setErrorMessage(err.message);
                    }
                    setIsloading(false)
                },
                signOut: async () => {
                    setIsloading(true)
                    try {
                        setErrorMessage(null)
                        await Auth.signOut();
                        setLoggedOutState()
                    } catch (err) {
                        console.error(err)
                        setErrorMessage(err.message);
                    }
                    setIsloading(false)
                },
                forgotPasswordSendEmail,
                forgotPasswordSetNewPassword,
                changePassword,
                jwt,
                email,
                errorMessage,
                isLoading
            }}>
            {children}
        </AuthContext.Provider>
    )
}